if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.targetButton = document.getElementById(this.dataset.targetButtonId);
        this.priceSource = document.getElementById(this.dataset.priceSourceId);
        this.addToCartButton = this.querySelector('.sticky-atc__button--add-to-cart');
        this.addToCartText = this.addToCartButton.querySelector('.sticky-atc__button-text');
        this.buyNowButton = this.querySelector('.sticky-atc__button--buy-now');
        this.buyNowText = this.buyNowButton.querySelector('.sticky-atc__button-text');
        this.stickyPrice = this.querySelector('.sticky-atc__price');

        if (!this.targetButton) return;

        this.defaultAddToCartLabel = this.addToCartText.textContent;

        this.syncPrice();
        this.syncAddToCartState();
        this.observeTarget();
        this.observePrice();
        this.observeAddToCartState();
        this.watchForBuyNowButton();

        this.addToCartButton.addEventListener('click', () => this.forwardClick(this.addToCartButton, this.targetButton));
        this.buyNowButton.addEventListener('click', () => this.forwardClick(this.buyNowButton, this.realBuyNowButton));
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.addToCartObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.buyNowContainerObserver?.disconnect();
      }

      observeTarget() {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('sticky-atc--visible', scrolledPast);
            this.hidden = false;
          },
          { threshold: 0 }
        );
        this.intersectionObserver.observe(this.targetButton);
      }

      observePrice() {
        if (!this.priceSource) return;
        this.priceObserver = new MutationObserver(() => this.syncPrice());
        this.priceObserver.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
      }

      observeAddToCartState() {
        this.addToCartObserver = new MutationObserver(() => this.syncAddToCartState());
        this.addToCartObserver.observe(this.targetButton, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      // The dynamic checkout button ("Buy it now") is injected asynchronously by
      // Shopify's own checkout script, so it may not exist yet when this element
      // connects. Watch the real form until it appears, then wire it up. If it
      // never appears (dynamic checkout disabled for this product), the sticky
      // buy-now button simply stays hidden.
      watchForBuyNowButton() {
        const form = this.targetButton.closest('product-form');
        if (!form) return;

        const tryWire = () => {
          const button = form.querySelector('.shopify-payment-button__button');
          if (button) {
            this.realBuyNowButton = button;
            this.syncBuyNowState();
            this.observeBuyNowState();
            this.buyNowContainerObserver?.disconnect();
            return true;
          }
          return false;
        };

        if (tryWire()) return;

        this.buyNowContainerObserver = new MutationObserver(() => tryWire());
        this.buyNowContainerObserver.observe(form, { childList: true, subtree: true });
      }

      observeBuyNowState() {
        this.buyNowStateObserver?.disconnect();
        this.buyNowStateObserver = new MutationObserver(() => this.syncBuyNowState());
        this.buyNowStateObserver.observe(this.realBuyNowButton, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      syncPrice() {
        if (this.priceSource && this.stickyPrice) {
          this.stickyPrice.innerHTML = this.priceSource.innerHTML;
        }
      }

      syncAddToCartState() {
        const disabled = this.targetButton.hasAttribute('disabled');
        this.addToCartButton.disabled = disabled;
        const targetLabel = this.targetButton.querySelector('span')?.textContent?.trim();
        this.addToCartText.textContent = targetLabel || this.defaultAddToCartLabel;
      }

      syncBuyNowState() {
        const label = this.realBuyNowButton.textContent.trim();
        this.buyNowText.textContent = label;
        this.buyNowButton.disabled = this.realBuyNowButton.disabled;
        this.buyNowButton.hidden = false;
      }

      forwardClick(stickyButton, realButton) {
        if (!realButton || realButton.disabled) return;
        stickyButton.classList.add('is-loading');
        realButton.click();
        const stopLoading = () => stickyButton.classList.remove('is-loading');
        // Buy-now navigates to checkout almost immediately; add-to-cart toggles
        // a "loading" class on the real button while the AJAX request runs.
        const loadingObserver = new MutationObserver(() => {
          if (!realButton.classList.contains('loading')) {
            stopLoading();
            loadingObserver.disconnect();
          }
        });
        loadingObserver.observe(realButton, { attributes: true, attributeFilter: ['class'] });
        setTimeout(stopLoading, 4000);
      }
    }
  );
}
